import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ErroreApi, chiama } from '../api/cliente.js';
import {
  type Anteprima,
  type EsitoCaricamento,
  type EsitoImportazione,
  type Foglio,
  type Mappatura,
  type SceltaAltroCliente,
  useAnteprimaImport,
  useCambiaFoglio,
  useCampiMappabili,
  useCaricaFoglio,
  useImportaOrdini,
} from '../api/import-excel.js';
import { useDestinazioniCliente } from '../api/ordini-scrittura.js';
import { voceDestinazione } from '../componenti/SchedaDestinazione.js';
import { ZonaFile } from '../componenti/ZonaFile.js';
import { data } from '../formati.js';
import { locale, t } from '../i18n/lingua.js';

/**
 * Importare gli ordini da un foglio Excel.
 *
 * È l'operazione che fanno più spesso e quella per cui telefonano. Nel v2 si
 * carica il file, si preme importa, e si scopre dopo cosa è andato storto:
 * il foglio sbagliato, una colonna che ha cambiato nome, un codice che non è
 * in listino.
 *
 * Qui le domande arrivano nell'ordine in cui uno se le fa:
 *
 *  1. **di chi è** — il cliente per primo, perché è lui a decidere come si
 *     leggono i codici articolo e da che listino vengono i prezzi;
 *  2. **quale foglio** — non sempre è il primo, e dentro lo stesso file c'è
 *     un «POs to Delete» che importare sarebbe il contrario di quel che serve;
 *  3. **quale colonna è cosa** — proposta da sola, e ricordata dalla volta
 *     prima, così di solito si guarda e si va avanti;
 *  4. **cosa succederebbe** — quanti ordini, con quali articoli, a quali
 *     prezzi, e quali righe hanno un problema;
 *  5. e solo allora si scrive.
 *
 * Indietro si può sempre tornare: il file resta caricato, e cambiare una
 * colonna non costa un nuovo caricamento.
 */

const PASSI = [
  t('Cliente'),
  t('File'),
  t('Foglio'),
  t('Colonne'),
  t('Anteprima'),
] as const;

/** Un prezzo unitario: tre decimali, come sulle righe d'ordine. */
const prezzo = (valore: number): string =>
  valore.toLocaleString('it-IT', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

export function ImportOrdiniExcel() {
  const navigate = useNavigate();
  const campi = useCampiMappabili();
  const carica = useCaricaFoglio();
  const cambiaFoglio = useCambiaFoglio();
  const chiediAnteprima = useAnteprimaImport();
  const importa = useImportaOrdini();

  const [passo, setPasso] = useState(0);
  const [cerca, setCerca] = useState('');
  const [cliente, setCliente] = useState<{ id: number; name: string } | null>(null);
  const [sezionale, setSezionale] = useState('');
  /**
   * Il sezionale è quello del cliente, come in «Nuovo ordine»: decide a quale
   * società di Business Central va l'ordine, e qui si poteva scegliere anche
   * l'altro. Si sceglie a mano solo se il cliente non ne ha uno.
   */
  const predefiniti = useQuery({
    queryKey: ['predefiniti-ordine', cliente?.id],
    queryFn: () =>
      chiama<{ sectionalId: number | null; sectional: string | null }>(
        `/api/customers/${cliente!.id}/order-defaults`,
      ),
    enabled: cliente !== null,
  });
  const sezionaleDelCliente = predefiniti.data?.sectionalId ?? null;
  const sezionaleScelto = sezionaleDelCliente !== null ? String(sezionaleDelCliente) : sezionale;
  const [file, setFile] = useState<File | null>(null);

  const [caricato, setCaricato] = useState<EsitoCaricamento | null>(null);
  const [foglio, setFoglio] = useState('');
  const [rigaTitoli, setRigaTitoli] = useState(1);
  const [colonne, setColonne] = useState<Foglio['colonne']>([]);
  const [mappatura, setMappatura] = useState<Mappatura>({});
  const [ricorda, setRicorda] = useState(true);
  const [anteprima, setAnteprima] = useState<Anteprima | null>(null);
  const [stabilimenti, setStabilimenti] = useState<Record<string, number>>({});
  const [altriClienti, setAltriClienti] = useState<Record<string, SceltaAltroCliente>>({});
  const [esito, setEsito] = useState<EsitoImportazione | null>(null);

  const clienti = useQuery({
    queryKey: ['clienti-per-import', cerca],
    queryFn: () =>
      chiama<{ rows: { id: number; name: string; bcCodeSpa: string | null }[] }>(
        `/api/identities?type=customer&size=15&q=${encodeURIComponent(cerca)}`,
      ),
    enabled: cerca.trim().length >= 2,
  });

  const destinazioni = useDestinazioniCliente(cliente?.id ?? null);

  const opzioni = useQuery({
    queryKey: ['opzioni-ordine'],
    queryFn: () =>
      chiama<{ options: Record<string, { id: string | number; label: string }[]> }>(
        '/api/orders/filters',
      ),
    staleTime: 10 * 60_000,
  });

  const errore = [carica.error, cambiaFoglio.error, chiediAnteprima.error, importa.error].find(
    (e): e is ErroreApi => e instanceof ErroreApi,
  );

  const obbligatoriMancanti = useMemo(
    () =>
      (campi.data?.fields ?? [])
        .filter((c) => c.obbligatorio && !mappatura[c.chiave])
        .map((c) => c.etichetta),
    [campi.data, mappatura],
  );

  /** Passo 2 → 3: si carica il file e si guarda cosa c'è dentro. */
  const caricaIlFile = () => {
    if (!file || !cliente) return;
    carica.mutate(
      { file, customerId: cliente.id },
      {
        onSuccess: (risposta) => {
          setCaricato(risposta);
          // gli stabilimenti detti le volte scorse: non si richiedono
          setStabilimenti(risposta.plants ?? {});
          // le risposte sui PO di altri clienti valgono per questo file soltanto
          setAltriClienti({});
          setFoglio(risposta.suggestedSheet);
          setMappatura(risposta.mapping);
          setColonne(
            risposta.sheets.find((f) => f.nome === risposta.suggestedSheet)?.colonne ?? [],
          );
          setPasso(2);
        },
      },
    );
  };

  const scegliFoglio = (nome: string, riga = rigaTitoli) => {
    if (!caricato || !cliente) return;
    setFoglio(nome);
    cambiaFoglio.mutate(
      { token: caricato.token, sheet: nome, headerRow: riga, customerId: cliente.id },
      {
        onSuccess: (risposta) => {
          setColonne(risposta.sheet.colonne);
          setMappatura(risposta.mapping);
          setCaricato((c) =>
            c
              ? { ...c, sheets: c.sheets.map((f) => (f.nome === nome ? risposta.sheet : f)) }
              : c,
          );
        },
      },
    );
  };

  const guardaAnteprima = () => {
    if (!caricato || !cliente) return;
    chiediAnteprima.mutate(
      {
        token: caricato.token,
        customerId: cliente.id,
        sheet: foglio,
        headerRow: rigaTitoli,
        mapping: mappatura,
        plantAddresses: stabilimenti,
        otherCustomerChoices: altriClienti,
      },
      {
        onSuccess: (risposta) => {
          setAnteprima(risposta);
          setPasso(4);
        },
      },
    );
  };

  const conferma = () => {
    if (!caricato || !cliente || !sezionaleScelto) return;
    importa.mutate(
      {
        token: caricato.token,
        customerId: cliente.id,
        sheet: foglio,
        headerRow: rigaTitoli,
        mapping: mappatura,
        plantAddresses: stabilimenti,
        otherCustomerChoices: altriClienti,
        sectionalId: Number(sezionaleScelto),
        remember: ricorda,
      },
      { onSuccess: setEsito },
    );
  };

  const foglioScelto = caricato?.sheets.find((f) => f.nome === foglio) ?? null;

  /**
   * Quello che la conferma farebbe: gli ordini nuovi, e le consegne da
   * aggiornare — quelle dello stesso cliente, più quelle degli altri clienti
   * per cui si è detto «aggiorna». Un foglio rimandato solo per cambiare le
   * consegne non crea niente, ma ha qualcosa da fare.
   */
  const senzaRisposta = (anteprima?.otherCustomerOrders ?? []).filter((n) => !altriClienti[n]);
  const consegneDaAggiornare =
    (anteprima?.deliveriesToUpdate ?? 0) +
    (anteprima?.otherCustomerOrders ?? []).filter((n) => altriClienti[n] === 'update').length;
  const daFare = (anteprima?.ordersToCreate ?? 0) + consegneDaAggiornare;

  // ————————————————————————————————————— l'esito, a cose fatte

  if (esito) {
    return (
      <div className="page">
        <div className="page-head">
          <div>
            <h1 className="page-title">{t('Importazione finita')}</h1>
            <div className="page-sub">
              {t('{n} ordini creati per {cliente}', { n: esito.created.length, cliente: cliente?.name ?? '' })}
            </div>
          </div>
          <div className="page-actions">
            <Link className="btn" to="/sales-orders">
              {t('Vai agli ordini')}
            </Link>
            <button
              className="btn btn-primary"
              onClick={() => {
                setEsito(null);
                setCaricato(null);
                setFile(null);
                setAnteprima(null);
                setPasso(0);
              }}
            >
              {t('Importane un altro')}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2 className="card-title">{t('Ordini creati')}</h2>
            <span className="dim">{esito.created.length}</span>
          </div>
          <div className="card-body tight">
            {esito.created.length === 0 ? (
              <div className="empty">{t('Nessun ordine è stato creato.')}</div>
            ) : (
              <ul className="esiti">
                {esito.created.map((o) => (
                  <li key={o.orderId}>
                    <Link
                      className="cell-link"
                      to="/sales-orders/$id"
                      params={{ id: String(o.orderId) }}
                    >
                      {o.orderNumber}
                    </Link>
                    <span className="dim">
                      {o.lines} {o.lines === 1 ? 'riga' : 'righe'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {esito.updated.length > 0 && (
          <div className="card">
            <div className="card-head">
              <h2 className="card-title">{t('Consegna richiesta aggiornata')}</h2>
              <span className="dim">{esito.updated.length}</span>
            </div>
            <div className="card-body tight">
              <ul className="esiti">
                {esito.updated.map((o) => (
                  <li key={o.orderId}>
                    <Link
                      className="cell-link"
                      to="/sales-orders/$id"
                      params={{ id: String(o.orderId) }}
                    >
                      {o.orderNumber}
                    </Link>
                    <span className="dim">
                      {o.from ? data(o.from) : '—'} → {data(o.to)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {(esito.skipped.length > 0 || esito.linesSkipped.length > 0) && (
          <div className="card" style={{ marginTop: 14 }}>
            <div className="card-head">
              <h2 className="card-title">{t('Cosa è rimasto fuori')}</h2>
              <span className="dim">
                {t('{n} voci', { n: esito.skipped.length + esito.linesSkipped.length })}
              </span>
            </div>
            <div className="card-body tight">
              {/* saltato in silenzio è peggio che non importato: si dice tutto,
                  con il perché accanto */}
              <ul className="esiti">
                {esito.skipped.map((s) => (
                  <li key={`o-${s.orderNumber}`}>
                    <strong>{s.orderNumber}</strong>
                    <span className="dim">{s.reason}</span>
                  </li>
                ))}
                {esito.linesSkipped.map((r) => (
                  <li key={`r-${r.orderNumber}-${r.row}`}>
                    <span>
                      {r.orderNumber} · {t('riga {n}', { n: r.row })}
                    </span>
                    <span className="dim">{r.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ————————————————————————————————————— la procedura

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <Link className="back-link" to="/sales-orders">
            {t('← Ordini')}
          </Link>
          <h1 className="page-title">{t('Importa ordini da Excel')}</h1>
          <div className="page-sub">
            {cliente ? cliente.name : t('Prima di tutto: di chi è questo foglio')}
            {caricato && ` · ${caricato.fileName}`}
          </div>
        </div>
      </div>

      <ol className="passi">
        {PASSI.map((nome, i) => (
          <li key={nome} className={i === passo ? 'passo passo-ora' : i < passo ? 'passo passo-fatto' : 'passo'}>
            <button
              className="passo-btn"
              disabled={i > passo}
              onClick={() => setPasso(i)}
              aria-current={i === passo ? 'step' : undefined}
            >
              <span className="passo-num">{i + 1}</span>
              {nome}
            </button>
          </li>
        ))}
      </ol>

      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 14 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore.message}</span>
        </div>
      )}

      {/* 1 — il cliente */}
      {passo === 0 && (
        <div className="card">
          <div className="card-head">
            <h2 className="card-title">{t('Di chi è questo foglio')}</h2>
          </div>
          <div className="card-body">
            <p className="nota-prezzo">
              {t('Il cliente si sceglie per primo perché decide tutto il resto: i codici articolo del foglio si cercano nel')}{' '}
                  <strong>{t('suo')}</strong>{' '}
                  {t('listino, e da lì vengono i prezzi. Il prezzo scritto nell’Excel serve solo a controllare.')}
            </p>

            <div className="field span-2" style={{ marginBottom: 14 }}>
              <label htmlFor="imp-cliente">
                {t('Cliente')}<span className="req">*</span>
                <span className="dim" style={{ fontWeight: 400 }}>
                  {' '}
                  {t('— cerca per nome, codice BC o partita IVA')}
                </span>
              </label>
              {cliente ? (
                <div className="scelto">
                  <strong>{cliente.name}</strong>
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      setCliente(null);
                      setCaricato(null);
                    }}
                  >
                    {t('Cambia')}
                  </button>
                </div>
              ) : (
                <>
                  <input
                    id="imp-cliente"
                    className="input"
                    placeholder={t('Scrivi almeno due lettere')}
                    value={cerca}
                    autoFocus
                    onChange={(e) => setCerca(e.target.value)}
                  />
                  {(clienti.data?.rows ?? []).length > 0 && (
                    <ul className="risultati">
                      {clienti.data!.rows.map((c) => (
                        <li key={c.id}>
                          <button
                            className="risultato"
                            onClick={() => {
                              setCliente({ id: c.id, name: c.name });
                              setCerca('');
                            }}
                          >
                            <span>{c.name}</span>
                            {c.bcCodeSpa && <span className="dim num">{c.bcCodeSpa}</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>

            <div className="fields">
              <div className="field">
                <label htmlFor="imp-sez">
                  {t('Sezionale')}<span className="req">*</span>
                </label>
                <select
                  id="imp-sez"
                  className="select"
                  value={sezionaleScelto}
                  disabled={sezionaleDelCliente !== null}
                  onChange={(e) => setSezionale(e.target.value)}
                >
                  <option value="">{t('Scegli…')}</option>
                  {(opzioni.data?.options.sectionals ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <div className="hint">
                  {sezionaleDelCliente !== null
                    ? t('È quello del cliente: decide a quale società di Business Central vanno gli ordini.')
                    : t('Vale per tutti gli ordini di questo foglio.')}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-foot">
            <span className="spacer" />
            <button
              className="btn btn-primary"
              disabled={!cliente || !sezionaleScelto}
              onClick={() => setPasso(1)}
            >
              {t('Avanti')}
            </button>
          </div>
        </div>
      )}

      {/* 2 — il file */}
      {passo === 1 && (
        <div className="card">
          <div className="card-head">
            <h2 className="card-title">{t('Il foglio da importare')}</h2>
          </div>
          <div className="card-body">
            <ZonaFile
              file={file}
              estensioni={['.xlsx', '.xls', '.xlsm']}
              massimo={20 * 1024 * 1024}
              onFile={setFile}
            />
            <div className="hint" style={{ marginTop: 8 }}>
              {t('Il file resta qui finché non hai finito: cambiare foglio o colonna non lo ricarica.')}
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn" onClick={() => setPasso(0)}>
              {t('Indietro')}
            </button>
            <span className="spacer" />
            <button
              className="btn btn-primary"
              disabled={!file || carica.isPending}
              onClick={caricaIlFile}
            >
              {carica.isPending ? t('Leggo il file…') : t('Leggi il file')}
            </button>
          </div>
        </div>
      )}

      {/* 3 — il foglio */}
      {passo === 2 && caricato && (
        <div className="card">
          <div className="card-head">
            <h2 className="card-title">{t('Quale foglio')}</h2>
            <span className="dim">
              {caricato.sheets.length} {caricato.sheets.length === 1 ? 'foglio' : 'fogli'}
            </span>
          </div>
          <div className="card-body">
            <div className="fogli">
              {caricato.sheets.map((f) => {
                const sospetto = /delete|cancell|pivot/i.test(f.nome);
                return (
                  <button
                    key={f.nome}
                    className={f.nome === foglio ? 'foglio foglio-scelto' : 'foglio'}
                    onClick={() => scegliFoglio(f.nome)}
                    aria-pressed={f.nome === foglio}
                  >
                    <span className="foglio-nome">{f.nome}</span>
                    <span className="dim">
                      {t('{righe} righe · {colonne} colonne', { righe: f.righe.toLocaleString(locale()), colonne: f.colonne.length })}
                    </span>
                    {/* «POs to Delete» sta nello stesso file degli ordini da
                        importare: dirlo prima costa una riga */}
                    {sospetto && <span className="foglio-nota">{t('non sembra da importare')}</span>}
                    {f.nome === caricato.suggestedSheet && (
                      <span className="foglio-nota foglio-nota-ok">{t('proposto')}</span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="fields" style={{ marginTop: 16 }}>
              <div className="field">
                <label htmlFor="imp-riga">{t('Riga dei titoli')}</label>
                <input
                  id="imp-riga"
                  className="input"
                  type="number"
                  min={1}
                  max={50}
                  value={rigaTitoli}
                  onChange={(e) => setRigaTitoli(Math.max(1, Number(e.target.value) || 1))}
                  onBlur={() => scegliFoglio(foglio, rigaTitoli)}
                />
                <div className="hint">
                  {t('Quasi sempre la prima. Cambiala se in cima al foglio c’è un titolo o una riga vuota.')}
                </div>
              </div>
            </div>

            {foglioScelto && foglioScelto.anteprima.length > 0 && (
              <div className="table-wrap" style={{ marginTop: 14 }}>
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        {foglioScelto.colonne.map((c) => (
                          <th key={c.indice}>
                            <span className="dim">{c.lettera}</span> {c.titolo || '—'}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {foglioScelto.anteprima.map((riga, i) => (
                        <tr key={i}>
                          {riga.map((valore, j) => (
                            <td key={j}>{valore}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <div className="modal-foot">
            <button className="btn" onClick={() => setPasso(1)}>
              {t('Indietro')}
            </button>
            <span className="spacer" />
            <button
              className="btn btn-primary"
              disabled={!foglio || cambiaFoglio.isPending}
              onClick={() => setPasso(3)}
            >
              {t('Avanti')}
            </button>
          </div>
        </div>
      )}

      {/* 4 — le colonne */}
      {passo === 3 && (
        <div className="card">
          <div className="card-head">
            <h2 className="card-title">{t('Quale colonna è cosa')}</h2>
            {caricato?.remembered && (
              <span className="badge badge-ok">{t('come l’ultima volta')}</span>
            )}
          </div>
          <div className="card-body">
            <p className="nota-prezzo">
              {caricato?.remembered
                ? t('Le colonne sono già quelle dell’ultima importazione di questo cliente: controlla che i titoli non siano cambiati e vai avanti.')
                : t('Le colonne sono state riconosciute dai titoli. Correggi quelle sbagliate: la prossima volta le ritrovi già compilate.')}
            </p>

            <div className="mappa">
              {(campi.data?.fields ?? []).map((campo) => {
                const scelta = mappatura[campo.chiave] ?? '';
                const colonna = colonne.find((c) => c.titolo === scelta);
                return (
                  <div className="mappa-riga" key={campo.chiave}>
                    <div className="mappa-campo">
                      <label htmlFor={`mappa-${campo.chiave}`}>
                        {campo.etichetta}
                        {campo.obbligatorio && <span className="req">*</span>}
                      </label>
                      <div className="hint">{campo.aiuto}</div>
                    </div>
                    <div className="mappa-scelta">
                      <select
                        id={`mappa-${campo.chiave}`}
                        className="select"
                        value={scelta}
                        onChange={(e) =>
                          setMappatura((m) => ({
                            ...m,
                            [campo.chiave]: e.target.value || null,
                          }))
                        }
                      >
                        <option value="">
                          {campo.obbligatorio ? t('— manca —') : t('non c’è nel foglio')}
                        </option>
                        {colonne.map((c) => (
                          <option key={c.indice} value={c.titolo}>
                            {c.lettera} · {c.titolo || '(senza titolo)'}
                          </option>
                        ))}
                      </select>
                      {/* i valori d'esempio dicono più del titolo: è così che ci si
                          accorge di aver preso «Item» invece di «Material» */}
                      {colonna && colonna.esempi.length > 0 && (
                        <div className="mappa-esempi">{colonna.esempi.join(' · ')}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {obbligatoriMancanti.length > 0 && (
              <div className="avviso" role="status" style={{ marginTop: 14 }}>
                <span className="avviso-segno">▲</span>
                <span>{t('Senza queste non si può andare avanti: {campi}.', { campi: obbligatoriMancanti.join(', ') })}</span>
              </div>
            )}

            <div className="switch-row" style={{ marginTop: 16 }}>
              <button
                className="switch"
                role="switch"
                aria-checked={ricorda}
                aria-label={t('Ricorda queste colonne per questo cliente')}
                onClick={() => setRicorda((r) => !r)}
              />
              <span>
                {t('Ricorda queste colonne per')} <strong>{cliente?.name}</strong>{t(': la prossima volta le ritrovi compilate.')}
              </span>
            </div>
          </div>
          <div className="modal-foot">
            <button className="btn" onClick={() => setPasso(2)}>
              {t('Indietro')}
            </button>
            <span className="spacer" />
            <button
              className="btn btn-primary"
              disabled={obbligatoriMancanti.length > 0 || chiediAnteprima.isPending}
              onClick={guardaAnteprima}
            >
              {chiediAnteprima.isPending ? t('Controllo il foglio…') : t('Guarda cosa succede')}
            </button>
          </div>
        </div>
      )}

      {/* 5 — l'anteprima */}
      {passo === 4 && anteprima && (
        <>
          <div className="riepilogo">
            <div className="riepilogo-voce">
              <b>{anteprima.rowsRead.toLocaleString('it-IT')}</b>
              <span>{t('righe lette')}</span>
            </div>
            <div className="riepilogo-voce riepilogo-buono">
              <b>{anteprima.ordersToCreate.toLocaleString('it-IT')}</b>
              <span>{t('ordini da creare')}</span>
            </div>
            {anteprima.ordersAlreadyThere > 0 && (
              <div className="riepilogo-voce">
                <b>{anteprima.ordersAlreadyThere.toLocaleString('it-IT')}</b>
                <span>{t('già presenti')}</span>
              </div>
            )}
            {consegneDaAggiornare > 0 && (
              <div className="riepilogo-voce riepilogo-buono">
                <b>{consegneDaAggiornare.toLocaleString('it-IT')}</b>
                <span>{t('consegne da aggiornare')}</span>
              </div>
            )}
            {anteprima.rowsBlocked > 0 && (
              <div className="riepilogo-voce riepilogo-guaio">
                <b>{anteprima.rowsBlocked.toLocaleString('it-IT')}</b>
                <span>{t('righe che non entrano')}</span>
              </div>
            )}
            {anteprima.rowsWarned > 0 && (
              <div className="riepilogo-voce riepilogo-guarda">
                <b>{anteprima.rowsWarned.toLocaleString('it-IT')}</b>
                <span>{t('righe da guardare')}</span>
              </div>
            )}
          </div>

          {anteprima.problems.map((p, i) => (
            <div className="avviso" role="status" key={i} style={{ marginBottom: 12 }}>
              <span className="avviso-segno">▲</span>
              <span>{p.testo}</span>
            </div>
          ))}

          {/**
           * Gli stabilimenti che non si sa dove mandare.
           *
           * È la domanda che nel v2 diventava un errore: «Location Code non
           * associata a nessuna anagrafica», riga per riga, senza modo di
           * rispondere. Il nome del foglio e il nostro non combaciano quasi
           * mai — «DHL Fontana» da noi è «Fontana» — e indovinare non si può,
           * perché «Hopewell Dist Center - East» somiglia a «- West» e la
           * merce partirebbe per l'altra costa.
           *
           * Si risponde qui, una volta: da lì in poi lo sa.
           */}
          {/**
           * I PO che sono già di un altro cliente.
           *
           * Il numero d'ordine è unico nell'azienda, non per cliente: un PO del
           * foglio può essere l'ordine di qualcun altro, in qualunque stato.
           * Prima se ne cambiava la consegna senza dirlo; adesso si dice di chi
           * è e si chiede, uno per uno.
           */}
          {anteprima.otherCustomerOrders.length > 0 && (
            <div className="card ordine-anteprima" style={{ marginBottom: 14 }}>
              <div className="card-head">
                <h2 className="card-title">{t('PO che sono già di un altro cliente')}</h2>
                <span className="dim">{t('{n} da decidere', { n: senzaRisposta.length })}</span>
              </div>
              <div className="card-body">
                <p className="nota-prezzo">
                  {t(
                    'Questi numeri d’ordine ci sono già, ma non sono di {cliente}. Il foglio ne cambierebbe la consegna richiesta: dimmi cosa fare.',
                    { cliente: cliente?.name ?? '' },
                  )}
                </p>
                {anteprima.orders
                  .filter((o) => anteprima.otherCustomerOrders.includes(o.orderNumber))
                  .map((o) => (
                    <div className="mappa-riga" key={o.orderNumber}>
                      <div className="mappa-campo">
                        <strong>PO {o.orderNumber}</strong>
                        <div className="hint">
                          {t(
                            'è di {cliente}{codice} · ordine del {data}{stato} · consegna richiesta {prima}, nel foglio {dopo}',
                            {
                              cliente: o.otherCustomer!.name,
                              codice: o.otherCustomer!.bcCode ? ` (${o.otherCustomer!.bcCode})` : '',
                              data: data(o.otherCustomer!.orderDate),
                              stato: o.otherCustomer!.state ? ` · ${o.otherCustomer!.state}` : '',
                              prima: o.existingDelivery ? data(o.existingDelivery) : '—',
                              dopo: data(o.deliveryDate),
                            },
                          )}
                        </div>
                      </div>
                      <div
                        className="mappa-scelta"
                        role="radiogroup"
                        aria-label={`PO ${o.orderNumber}`}
                        style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                      >
                        <label className="check">
                          <input
                            type="radio"
                            name={`altro-${o.orderNumber}`}
                            checked={altriClienti[o.orderNumber] === 'skip'}
                            onChange={() => setAltriClienti((a) => ({ ...a, [o.orderNumber]: 'skip' }))}
                          />
                          <span>{t('Lascia stare quell’ordine')}</span>
                        </label>
                        <label className="check">
                          <input
                            type="radio"
                            name={`altro-${o.orderNumber}`}
                            checked={altriClienti[o.orderNumber] === 'update'}
                            onChange={() =>
                              setAltriClienti((a) => ({ ...a, [o.orderNumber]: 'update' }))
                            }
                          />
                          <span>{t('Aggiorna la sua consegna al {data}', { data: data(o.deliveryDate) })}</span>
                        </label>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {anteprima.unknownPlants.length > 0 && (
            <div className="card ordine-anteprima" style={{ marginBottom: 14 }}>
              <div className="card-head">
                <h2 className="card-title">{t('Dove va questa merce?')}</h2>
                <span className="dim">
                  {anteprima.unknownPlants.length === 1
                    ? t('1 stabilimento da associare')
                    : t('{n} stabilimenti da associare', { n: anteprima.unknownPlants.length })}
                </span>
              </div>
              <div className="card-body">
                <p className="nota-prezzo">
                  {t('Questi nomi non corrispondono a nessuna destinazione di {cliente}. Dillo una volta: la prossima volta li riconosce da solo.', {
                    cliente: cliente?.name ?? '',
                  })}
                </p>
                {anteprima.unknownPlants.map((ignoto) => (
                  <div className="mappa-riga" key={ignoto.plant}>
                    <div className="mappa-campo">
                      <label htmlFor={`dest-${ignoto.plant}`}>{ignoto.plant}</label>
                      <div className="hint">
                        {ignoto.rows === 1 ? t('1 riga nel foglio') : t('{n} righe nel foglio', { n: ignoto.rows })}
                      </div>
                    </div>
                    <div className="mappa-scelta">
                      <select
                        id={`dest-${ignoto.plant}`}
                        className="select"
                        value={stabilimenti[ignoto.plant] ?? ''}
                        onChange={(e) =>
                          setStabilimenti((s) => {
                            const nuovi = { ...s };
                            if (e.target.value) nuovi[ignoto.plant] = Number(e.target.value);
                            else delete nuovi[ignoto.plant];
                            return nuovi;
                          })
                        }
                      >
                        <option value="">{t('Scegli la destinazione…')}</option>
                        {(destinazioni.data?.destinations ?? []).map((d) => (
                          <option key={d.id} value={d.id}>
                            {voceDestinazione(d)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
              <div className="modal-foot">
                <span className="spacer" />
                <button
                  className="btn btn-primary"
                  disabled={
                    chiediAnteprima.isPending ||
                    anteprima.unknownPlants.some((i) => !stabilimenti[i.plant])
                  }
                  onClick={guardaAnteprima}
                >
                  {chiediAnteprima.isPending ? t('Ricontrollo…') : t('Ricontrolla il foglio')}
                </button>
              </div>
            </div>
          )}

          {/**
           * I prezzi che non combaciano.
           *
           * Vale il listino — è quello che fattura — ma se il cliente ordina
           * a un prezzo e noi vendiamo a un altro, la differenza salta fuori
           * in fattura, e a quel punto sono telefonate. Qui si vede prima di
           * confermare, per articolo e non per riga: quaranta righe dello
           * stesso codice sono una differenza sola da controllare.
           */}
          {anteprima.priceMismatches.length > 0 && (
            <div className="card ordine-anteprima" style={{ marginBottom: 14 }}>
              <div className="card-head">
                <h2 className="card-title">{t('Prezzi diversi dal listino')}</h2>
                <span className="dim">
                  {anteprima.priceMismatches.length}{' '}
                  {anteprima.priceMismatches.length === 1 ? 'articolo' : 'articoli'}
                </span>
              </div>
              <div className="card-body tight">
                <div className="table-wrap">
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>{t('Codice')}</th>
                          <th>{t('Articolo')}</th>
                          <th className="right">{t('Nel foglio')}</th>
                          <th className="right">{t('Nel listino')}</th>
                          <th className="right">{t('Differenza')}</th>
                          <th className="right">{t('Righe')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {anteprima.priceMismatches.map((d) => (
                          <tr key={`${d.articleCode}-${d.sheetPrice}-${d.listPrice}`}>
                            <td className="num">{d.articleCode}</td>
                            <td>{d.description ?? <span className="dim">—</span>}</td>
                            <td className="right num">{prezzo(d.sheetPrice)}</td>
                            <td className="right num strong">{prezzo(d.listPrice)}</td>
                            <td className="right num problema-guarda">
                              {d.listPrice > d.sheetPrice ? '+' : ''}
                              {prezzo(d.listPrice - d.sheetPrice)}
                            </td>
                            <td className="right num dim">{d.rows}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <p className="hint" style={{ marginTop: 10 }}>
                  {t('La differenza è quanto il listino sta sopra — o sotto — il foglio. Gli ordini entrano col prezzo del listino: se quello giusto è l’altro, il listino va corretto prima di importare.')}
                </p>
              </div>
            </div>
          )}

          {anteprima.orders.map((ordine) => {
            const bloccate = ordine.righe.filter((r) =>
              r.problemi.some((p) => p.gravita === 'bloccante'),
            ).length;
            return (
              <div className="card ordine-anteprima" key={ordine.orderNumber}>
                <div className="card-head">
                  <h2 className="card-title">
                    {ordine.orderNumber}
                    {ordine.giaPresente && (
                      <span className="badge badge-warn" style={{ marginLeft: 8 }}>
                        {t('c’è già')}
                      </span>
                    )}
                  </h2>
                  <span className="dim">
                    {ordine.righe.length === 1 ? t('1 riga') : t('{n} righe', { n: ordine.righe.length })}
                    {bloccate > 0 && ` · ${bloccate} da correggere`}
                    {ordine.addressLabel && ` · ${ordine.addressLabel}`}
                    {ordine.deliveryDate && ` · consegna ${ordine.deliveryDate}`}
                  </span>
                </div>
                {ordine.problemi.map((p, i) => (
                  <div className="card-body tight" key={i}>
                    <div className="hint">{p.testo}</div>
                  </div>
                ))}
                <div className="table-wrap">
                  <div className="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>{t('Riga')}</th>
                          <th>{t('Codice')}</th>
                          <th>{t('Articolo')}</th>
                          <th className="right">{t('Quantità')}</th>
                          <th className="right">{t('Prezzo del listino')}</th>
                          <th>{t('Cosa non va')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ordine.righe.map((r) => {
                          const ferma = r.problemi.some((p) => p.gravita === 'bloccante');
                          return (
                            <tr key={r.riga} className={ferma ? 'riga-ferma' : undefined}>
                              <td className="dim num">{r.riga}</td>
                              <td className="num">{r.articleCode}</td>
                              <td>
                                {r.articleDescription ?? (
                                  <span className="dim">{r.descrizioneFoglio ?? '—'}</span>
                                )}
                              </td>
                              <td className="right num">
                                {r.quantity?.toLocaleString('it-IT') ?? '—'}
                              </td>
                              <td
                                className={
                                  r.prezzoDiverso ? 'right num problema-guarda' : 'right num'
                                }
                                title={
                                  r.prezzoDiverso
                                    ? t('Nel foglio c’era {prezzo}', { prezzo: prezzo(r.prezzoNelFoglio ?? 0) })
                                    : undefined
                                }
                              >
                                {r.priceExw === null
                                  ? '—'
                                  : prezzo(Number(r.priceExw) + Number(r.costDdp ?? 0))}
                                {r.fromDestination && (
                                  <span className="dim" title={t('Prezzo specifico della destinazione')}>
                                    {' '}
                                    ▸
                                  </span>
                                )}
                              </td>
                              <td>
                                {r.problemi.map((p, i) => (
                                  <div
                                    key={i}
                                    className={
                                      p.gravita === 'bloccante' ? 'problema-blocca' : 'problema-guarda'
                                    }
                                  >
                                    {p.testo}
                                  </div>
                                ))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="modal-foot" style={{ position: 'sticky', bottom: 0 }}>
            <button className="btn" onClick={() => setPasso(3)}>
              {t('Torna alle colonne')}
            </button>
            <span className="spacer" />
            {/* finché c'è uno stabilimento senza destinazione non si scrive
                niente: metà delle righe partirebbe per il posto sbagliato */}
            <button
              className="btn btn-primary"
              disabled={
                daFare === 0 ||
                anteprima.unknownPlants.length > 0 ||
                senzaRisposta.length > 0 ||
                importa.isPending
              }
              onClick={conferma}
            >
              {importa.isPending
                ? t('Importo…')
                : anteprima.unknownPlants.length > 0
                  ? t('Prima dimmi dove va la merce')
                  : senzaRisposta.length > 0
                    ? t('Prima dimmi cosa fare dei PO di altri clienti')
                    : anteprima.ordersToCreate === 0
                      ? consegneDaAggiornare === 1
                        ? t('Aggiorna 1 consegna')
                        : t('Aggiorna {n} consegne', { n: consegneDaAggiornare })
                      : anteprima.ordersToCreate === 1
                        ? t('Crea 1 ordine')
                        : t('Crea {n} ordini', { n: anteprima.ordersToCreate })}
            </button>
          </div>
        </>
      )}

      {/* niente da importare: si dice, invece di lasciare un pulsante spento */}
      {passo === 4 &&
        anteprima &&
        daFare === 0 &&
        anteprima.unknownPlants.length === 0 &&
        senzaRisposta.length === 0 && (
        <div className="empty">
          {t('Da questo foglio non nasce nessun ordine nuovo. Controlla il foglio scelto e le colonne, oppure')}{' '}
          <button className="btn btn-ghost" onClick={() => void navigate({ to: '/sales-orders' })}>
            {t('torna agli ordini')}
          </button>
          .
        </div>
      )}
    </div>
  );
}
